/**
 * Subdomain-based tenant routing utilities.
 *
 * Each product surface has a tenant-prefixed host matching its name in the
 * header switcher (see specs/archive/surface-subdomains):
 *   - Capture (web) — {slug}.capture.systemdox.com (this SPA); the bare
 *     app.systemdox.com stays the neutral mobile / login entry.
 *   - Build (console) — {slug}.build.systemdox.com (this SPA).
 *   - Knowledge Base — the in-app reader at {slug}.systemdox.com/docs (this
 *     SPA), authenticated and access-governed (ADR-032: one surface, no
 *     separate published portal).
 * The bare {slug}.systemdox.com is the tenant's launch home (LaunchPage). In
 * development, use the ?tenant=slug query parameter as a fallback.
 */

import { SURFACES, type SurfaceKey } from '@systemdox/surface-header';

// Reserved workspace slugs — two roles, one set (sdx#1130):
//  1. Slug validation — MIRRORS shared-auth's slug_utils.RESERVED_SLUGS (the
//     server is the authority). Kept in sync so the client pre-check agrees
//     with what the create/rename call will accept. The old mirror had drifted:
//     16 server-reserved names (dashboard, support, staging, login, …) were
//     shown "available" client-side, then rejected on submit.
//  2. Subdomain routing — the surface / infra host labels must never resolve to
//     a tenant (build/capture/docs/kb/…) and must never be claimable as a slug
//     (a tenant "build" would collide with build.systemdox.com).
const RESERVED_SUBDOMAINS = new Set([
  // shared-auth slug_utils.RESERVED_SLUGS — keep in sync:
  'app', 'api', 'www', 'admin', 'auth', 'mail', 'docs', 'staging', 'dev',
  'beta', 'status', 'support', 'help', 'billing', 'login', 'signup', 'portal',
  'dashboard', 'console', 'cdn', 'static', 'assets', 'media', 'test',
  // surface / product hosts — routing + host-collision guard:
  'mcp', 'capture', 'build', 'document', 'kb',
]);

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;

/**
 * Client-side slug pre-check: 3-63 chars, lowercase alphanumeric and
 * hyphens, no leading/trailing hyphen, not an obviously reserved name.
 */
export function isValidSlug(slug: string): boolean {
  return SLUG_PATTERN.test(slug) && !RESERVED_SUBDOMAINS.has(slug);
}

/**
 * Suggest a slug from a workspace name: lowercase, non-alphanumerics
 * collapsed to single hyphens, trimmed to the 63-char limit. May still
 * fail isValidSlug (e.g. too short) — callers keep validating.
 */
export function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}

/**
 * Extract the tenant slug from the current hostname.
 *
 * Handles the surface hosts that run this SPA — {slug}.build.systemdox.com and
 * {slug}.capture.systemdox.com — and the bare {slug}.systemdox.com launch host.
 * Returns `null` on the tenant-neutral app.systemdox.com, reserved subdomains,
 * or localhost without a ?tenant param.
 */
export function getTenantSlug(): string | null {
  const hostname = window.location.hostname;

  // Surface hosts that run this SPA: {slug}.build / {slug}.capture
  const surfaceMatch = hostname.match(/^([a-z0-9][a-z0-9-]*[a-z0-9])\.(?:build|capture)\.systemdox\.com$/);
  if (surfaceMatch && !RESERVED_SUBDOMAINS.has(surfaceMatch[1])) {
    return surfaceMatch[1];
  }

  // Launch host: bare {slug}.systemdox.com (single label only)
  const match = hostname.match(/^([a-z0-9][a-z0-9-]*[a-z0-9])\.systemdox\.com$/);
  if (match && !RESERVED_SUBDOMAINS.has(match[1])) {
    return match[1];
  }

  // Development: ?tenant=slug query param
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return new URLSearchParams(window.location.search).get('tenant');
  }

  return null;
}

/**
 * Check if the current hostname is the tenant-neutral app root
 * (app.systemdox.com, or localhost without a tenant param).
 */
export function isAppDomain(): boolean {
  const hostname = window.location.hostname;
  if (hostname === 'app.systemdox.com') return true;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return !new URLSearchParams(window.location.search).has('tenant');
  }
  return false;
}

/**
 * URL of the capture surface. The bare app.systemdox.com is the neutral mobile
 * / login entry; a known tenant gets the branded web capture host
 * {slug}.capture.systemdox.com. In development it points at the same origin with
 * `?capture` so the host switch renders capture.
 */
export function captureAppUrl(path = '/', slug?: string | null): string {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('capture', '');
    return url.toString();
  }
  if (slug) {
    return `https://${slug}.capture.systemdox.com${path}`;
  }
  return `https://app.systemdox.com${path}`;
}

/**
 * True on a capture-app host: the neutral app.systemdox.com (mobile-first
 * requirements/design capture surface, two-surface model #554) or a tenant's
 * web capture host {slug}.capture.systemdox.com — both render this surface,
 * distinct from the tenant console. In development, `?capture` forces capture.
 */
export function isCaptureHost(): boolean {
  const hostname = window.location.hostname;
  if (hostname === 'app.systemdox.com') return true;
  if (/^[a-z0-9][a-z0-9-]*[a-z0-9]\.capture\.systemdox\.com$/.test(hostname)) return true;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return new URLSearchParams(window.location.search).has('capture');
  }
  return false;
}

/**
 * True on a tenant's Build console host — {slug}.build.systemdox.com. The
 * console's pages moved onto the tenant root ({slug}.systemdox.com); this host
 * now only redirects there, keeping old URLs alive. In development, `?build`
 * forces it — the counterpart of `?capture`/`?launch`.
 */
export function isBuildHost(): boolean {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return new URLSearchParams(window.location.search).has('build');
  }
  return /^[a-z0-9][a-z0-9-]*[a-z0-9]\.build\.systemdox\.com$/.test(hostname);
}

/**
 * True on a tenant's web capture host — {slug}.capture.systemdox.com — but NOT
 * the tenant-neutral app.systemdox.com login entry. The capture Add workflow
 * moved onto the tenant root (/add); this host now only redirects there. Defined
 * as the capture host minus the neutral app entry.
 */
export function isTenantCaptureHost(): boolean {
  return isCaptureHost() && !isAppDomain();
}

/**
 * True on a tenant's launch host — the bare {slug}.systemdox.com, which now
 * serves the whole app: the LaunchPage hub, the Add · Docs · Gaps content
 * pages, and the console pages (Plan, Standards, Validate, …) that moved here
 * off {slug}.build. app.systemdox.com and the surface labels are
 * reserved so they never match. In development, `?launch` forces the launch
 * host (the counterpart of `?capture`), so the launch-only pages are runnable
 * locally.
 */
export function isLaunchHost(): boolean {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return new URLSearchParams(window.location.search).has('launch');
  }
  const m = hostname.match(/^([a-z0-9][a-z0-9-]*[a-z0-9])\.systemdox\.com$/);
  return !!(m && !RESERVED_SUBDOMAINS.has(m[1]));
}

/**
 * URL of a tenant's launch host (its home/hub — the LaunchPage — and the
 * Docs/Gaps pages): the bare {slug}.systemdox.com root. In development it stays
 * on the current origin with `?launch` as the host selection, keeping the given
 * `path` so deep links (e.g. /docs?doc=…) survive the hop.
 */
export function launchUrl(slug: string, path = '/'): string {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('launch', '');
    url.searchParams.set('tenant', slug);
    return url.toString();
  }
  return `https://${slug}.systemdox.com${path}`;
}

/**
 * Build the full URL for a tenant's console (the Build surface).
 */
export function tenantUrl(slug: string, path = '/'): string {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('tenant', slug);
    return url.toString();
  }
  return `https://${slug}.build.systemdox.com${path}`;
}

/**
 * Host portion of a tenant's console (Build) URL (for display).
 */
export function tenantHost(slug: string): string {
  return new URL(tenantUrl(slug)).host;
}

/**
 * The host suffix a slug maps to on the Build surface, for input adornments
 * that render `{slug}` + suffix (e.g. ".build.systemdox.com"). Falls back to
 * ".systemdox.com" off a production host (localhost/dev) so the adornment stays
 * clean.
 */
export function tenantHostSuffix(): string {
  const host = tenantHost('slug');
  return host.startsWith('slug.') ? host.slice('slug'.length) : '.systemdox.com';
}

/**
 * The host suffix a slug maps to on the tenant's launch home — the bare
 * `{slug}.systemdox.com` — for input adornments on signup, where the slug
 * names the whole workspace rather than one surface. Same localhost fallback
 * as `tenantHostSuffix`.
 */
export function launchHostSuffix(): string {
  const host = new URL(launchUrl('slug')).host;
  return host.startsWith('slug.') ? host.slice('slug'.length) : '.systemdox.com';
}

// Remembering the tenant slug across surfaces.
//
// The neutral capture app (app.systemdox.com) is on a reserved subdomain, so it
// can't derive the slug from its hostname, and its auth-token tenant may not
// have resolved yet — which left the Build/Document switches greyed out there.
// We persist the slug in a cookie scoped to the parent domain (.systemdox.com)
// so every surface host (app / capture / build / document) can read it and
// always link back.
const SLUG_COOKIE = 'sdx_tenant_slug';

/** Persist the active tenant slug for cross-subdomain use (prod only). */
export function rememberTenantSlug(slug: string): void {
  if (!slug) return;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return;
  // Share across app/console/{slug} subdomains; 1-year, refreshed on each set.
  document.cookie =
    `${SLUG_COOKIE}=${encodeURIComponent(slug)}; domain=.systemdox.com; path=/; ` +
    `max-age=31536000; SameSite=Lax; Secure`;
}

/** The last tenant slug remembered on this browser, if any. */
export function rememberedTenantSlug(): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + SLUG_COOKIE + '=([^;]+)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/** The three product surfaces of the unified header switcher. */
export type { SurfaceKey } from '@systemdox/surface-header';

export interface SurfaceLink {
  key: SurfaceKey;
  label: string;
  /** Absolute URL, or null when unavailable (e.g. docs needs a slug). */
  url: string | null;
}

/**
 * The surface this React bundle is currently rendering. The app bundle serves
 * the capture app (app.systemdox.com / {slug}.capture), the tenant console
 * ({slug}.build) and the launch home on the tenant's bare host; the Knowledge
 * Base reader is a route (/docs) within these, not a distinct surface host, so
 * this never returns `'docs'`. On
 * the launch host NO surface is current — the launch home is the hub the
 * header brand links to, not a switcher entry — so it returns null and the
 * switcher renders all three surfaces as links (treating it as `'console'`
 * used to render Build as the non-navigating active item there).
 */
export function currentSurface(): SurfaceKey | null {
  if (isLaunchHost()) return null;
  return isCaptureHost() ? 'app' : 'console';
}

/**
 * The three cross-surface destinations for the unified header. Order and
 * labels come from `@systemdox/surface-header` — the shared source of truth —
 * so the switcher cannot drift between surfaces. Only the URLs are computed
 * here: `console` and `docs` are per-tenant, so they carry a null url when the
 * slug is unknown and the switcher renders them disabled.
 */
export function surfaceLinks(slug: string | null): SurfaceLink[] {
  const urlFor = (key: SurfaceKey): string | null => {
    switch (key) {
      case 'app':
        // Capture: the tenant's branded web host when the slug is known
        // (post-cutover), else the neutral app.systemdox.com.
        return captureAppUrl('/', slug);
      case 'console':
        return slug ? tenantUrl(slug) : null;
      case 'docs':
        // Knowledge Base — the in-app reader on the tenant host (ADR-032: one
        // authenticated surface, no separate portal).
        return slug ? tenantUrl(slug, '/docs') : null;
    }
  };
  return SURFACES.map(({ key, label }) => ({ key, label, url: urlFor(key) }));
}

/** The neutral host: the only SystemDox origin Cognito returns to after sign-in or sign-out. */
export const APP_ORIGIN = 'https://app.systemdox.com';

/**
 * True on a production host other than app.systemdox.com: a workspace
 * ({slug}.systemdox.com) or one of its forwarding hosts. The app client
 * registers app.systemdox.com alone as a sign-in and sign-out return address,
 * and Cognito cannot register a wildcard, so these hosts sign in and out
 * through it (2026-10-25: a sign-in started on puglieseweb.systemdox.com
 * answered `redirect_mismatch`). Preprod is one registered host
 * (app.preprod.systemdox.com), and localhost signs in where it is.
 */
export function signsInThroughAppHost(hostname: string = window.location.hostname): boolean {
  return (
    hostname.endsWith('.systemdox.com') &&
    hostname !== 'app.systemdox.com' &&
    !hostname.endsWith('.preprod.systemdox.com')
  );
}
